import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { fetchAllPages, isApiError } from '../../api/client';
import { useApi } from '../../api/context';
import type { CreateNetworkRunRequest, Network, Run, RunSummary } from '../../api/types';
import { isTerminalRunStatus } from '../../api/types';
import { usePermission } from '../../auth/context';
import { useToast } from '../../components/toast';
import { strings } from '../../strings';
import { networkErrorInfo, useManageNetworks } from '../shared';

/** Every attachment of the network (the diagram and the phone list need them all), under the network's key. */
export function useAllAttachments(id: string, refetchInterval: number | false = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['network', id, 'attachments', 'all'],
    queryFn: () => fetchAllPages((page) => api.listAttachments(id, {}, page)),
    enabled: !!id,
    refetchInterval,
  });
}

/** A run of the network by ID (the active run's progress, the last apply's author, a plan's result). */
export function useRun(id: string | null | undefined, refetchInterval: number | false = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['run', id ?? ''],
    queryFn: () => api.getRun(id!),
    enabled: !!id,
    refetchInterval,
  });
}

/** Whether a run is a user-created full run (the kind `NETWORK_RUN_PENDING` is about). */
export function isUserFullRun(run: Pick<RunSummary, 'triggeredBy' | 'scope'>): boolean {
  return run.triggeredBy === 'user' && (run.scope ?? 'full') === 'full';
}

/** The latest completed full plan of the network in a page of its runs, newest first. */
export function latestFullPlan(runs: readonly RunSummary[] | undefined): RunSummary | null {
  return (
    runs?.find((r) => r.kind === 'plan' && r.status === 'completed' && (r.scope ?? 'full') === 'full') ?? null
  );
}

/** A plan is current while the network's settings hash equals the plan's and no apply finished after it. */
export function isCurrentPlan(
  plan: Pick<RunSummary, 'settingsHash' | 'finishedAt'>,
  network: Network,
): boolean {
  if (plan.settingsHash !== network.settingsHash) return false;
  const applied = network.lastSuccessfulApply?.completedAt;
  return !applied || !plan.finishedAt || Date.parse(plan.finishedAt) >= Date.parse(applied);
}

/** Starts a network run, refreshes the network's queries and opens the run (spec 11 6.15.4). */
export function useStartNetworkRun(network: Pick<Network, 'id' | 'name'>) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const start = useCallback(
    async (request: CreateNetworkRunRequest): Promise<Run | null> => {
      setBusy(true);
      try {
        const run = await api.createNetworkRun(network.id, request);
        void queryClient.invalidateQueries({ queryKey: ['network', network.id] });
        void queryClient.invalidateQueries({ queryKey: ['networks'] });
        void queryClient.invalidateQueries({ queryKey: ['runs'] });
        navigate(`/runs/${run.id}`);
        return run;
      } catch (error) {
        const info = isApiError(error)
          ? networkErrorInfo(error.code, { network: network.name, message: error.detail })
          : null;
        if (info) toast.show({ tone: 'error', title: info.sentence });
        else toast.error(error);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [api, navigate, network.id, network.name, queryClient, toast],
  );
  return { start, busy };
}

/** The pending or running user-created full run that blocks Plan, Apply… and Refresh (`NETWORK_RUN_PENDING`). */
export function blockingRun(active: readonly RunSummary[] | undefined): RunSummary | null {
  return active?.find((r) => !isTerminalRunStatus(r.status) && isUserFullRun(r)) ?? null;
}

/**
 * Why the user can't open Attach deployments…, or null (spec 11 6.15.6): network admins may (rows they can't attach
 * say why), and operators while the network's attach policy is `operators`.
 */
export function useAttachReason(network: Pick<Network, 'name' | 'settings'>): string | null {
  const operate = usePermission('operate');
  const manage = useManageNetworks();
  if (manage) return null;
  if (!operate) return strings.networks.page.requiresOperator;
  if (network.settings.attachPolicy === 'network-admins')
    return strings.networks.page.attachPolicyOnlyAdmins(network.name);
  return null;
}
